import React, { useState } from "react";
import { Modal, Container, Form, Row, Col, FormText } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import axios from "axios";
import {
    Elements,
    useStripe,
    useElements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
} from "@stripe/react-stripe-js";

import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function StripeCheckoutForm({ amount }) {
    const stripe = useStripe();
    const elements = useElements();

    const [paymentEmail, setPaymentEmail] = useState("");
    const [name, setName] = useState("");
    const [expiryComplete, setExpiryComplete] = useState(false);
    const [cvcComplete, setCvcComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const [showCardError, setShowCardError] = useState({
        card: undefined,
        expiry: undefined,
        securityCode: undefined,
    });
    
    const randomCode = () => {
        const chars =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let code = "";
        for (let i = 0; i < 8; i++) {
            code += chars[Math.floor(Math.random() * chars.length)];
        }
        return code;
    };

    const handleCardChange = (event) => {
        console.log("Event : ", event)
        if (event.complete) {
            setCardComplete(true);
        } else {
            setCardComplete(false);
        }

        if (event.error) {
            setShowCardError((prev) => ({
                ...prev,
                card: event.error,
            }));
        } else {
            setShowCardError((prev) => ({
                ...prev,
                card: undefined,
            }));
        }
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();

        console.log("👉 Submit clicked");

        if (!stripe || !elements) {
            console.log("❌ Stripe or Elements not ready");
            return;
        }

        console.log("✅ Stripe ready");

        const cardElement = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        if (!cardElement) {
            console.log("❌ Card element missing");
            return;
        }

        if (showCardError.card || showCardError.expiry || showCardError.securityCode) {
            console.log("❌ Card validation errors", showCardError);
            return;
        }

        try {
            setIsLoading(true);
            setIsProcessing(true);

            const code = randomCode();
            console.log("🔑 Confirmation code:", code);

            // 1️⃣ Create payment method
            const pmResult = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
                billing_details: {
                    name: name,
                    email: paymentEmail,
                },
            });

            if (pmResult.error) {
                console.log("❌ PaymentMethod error:", pmResult.error.message);
                setIsProcessing(false);
                setIsLoading(false);
                return;
            }

            const paymentMethod = pmResult.paymentMethod;
            console.log("✅ PaymentMethod created:", paymentMethod.id);

            // 2️⃣ Build payload (simplified for modal)
            const payload = {
                payment_method: paymentMethod.id,
                amount: 20,
                // amount: amount,
                confirmationCode: code,
            };

            console.log("📦 Payload → backend:", payload);

            // 3️⃣ Call backend to create payment intent
            const res = await axios.post(
                process.env.REACT_APP_SERVER + "stripe/payment-intent",
                payload
            );

            console.log("✅ Backend response:", res);

            const clientSecret = res.data.client_secret;

            if (!clientSecret) {
                console.log("❌ No client secret returned");
                setIsProcessing(false);
                setIsLoading(false);
                return;
            }

            // 4️⃣ Confirm card payment
            const confirm = await stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethod.id,
            });

            if (confirm.error) {
                console.log("❌ Confirm error:", confirm.error.message);
                setIsProcessing(false);
                setIsLoading(false);
                return;
            }

            if (confirm.paymentIntent.status === "succeeded") {
                console.log("🎉 Payment succeeded");

                elements.getElement(CardNumberElement)?.clear();
                elements.getElement(CardExpiryElement)?.clear();
                elements.getElement(CardCvcElement)?.clear();

                setName("");
                setPaymentEmail("");

                setIsProcessing(false);
                setIsLoading(false);
            }
        } catch (err) {
            console.log("❌ Payment exception:", err);
            setIsProcessing(false);
            setIsLoading(false);
        }
    };
    return (
        <div className="common-font-section">
            <Container>
                <Form onSubmit={handlePaymentSubmit}>
                    <Form.Group as={Row}>
                        <Col md={3}>
                            <Form.Label>Email:</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Form.Control
                                type="email"
                                value={paymentEmail}
                                onChange={(e) => setPaymentEmail(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row}>
                        <Col md={3}>
                            <Form.Label>Full Name:</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Form.Control
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label className="mb-2">Card Number:</Form.Label>
                        <div className="cardNumber_field">
                            <CardNumberElement
                                className="data-card"
                                onChange={handleCardChange}
                                options={{
                                    style: {
                                        base: {
                                            fontSize: "17px",
                                            color: "#000",
                                            "::placeholder": { color: "#999" },
                                        },
                                    },
                                }}
                            />
                        </div>
                    </Form.Group>

                    <div className="card_extra_details d-flex mb-3">
                        <div className="w-25 mr-2">
                            <Form.Label>Expires:</Form.Label>
                            {/* <CardExpiryElement /> */}
                            <CardExpiryElement
                                onChange={(event) => {
                                    setExpiryComplete(event.complete);
                                    setShowCardError(prev => ({
                                        ...prev,
                                        expiry: event.error
                                    }));
                                }}
                            />
                            {((showCardError && showCardError.expiry) && isLoading) && (
                                <FormText className="text-danger">{showCardError.expiry.message}</FormText>
                            )}
                        </div>

                        <div className="w-25 ml-2">
                            <Form.Label>CVC:</Form.Label>
                            {/* <CardCvcElement /> */}
                            <CardCvcElement
                                onChange={(event) => {
                                    setCvcComplete(event.complete);
                                    setShowCardError(prev => ({
                                        ...prev,
                                        securityCode: event.error
                                    }));
                                }}
                            />
                            {((showCardError && showCardError.securityCode) && isLoading) && (
                                <FormText className="text-danger">{showCardError.securityCode.message}</FormText>
                            )}
                        </div>
                    </div>

                    {showCardError.card && cardComplete === false && (
                        <div style={{ color: "red", marginBottom: 10 }}>
                            {showCardError.card.message}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={!cardComplete || !expiryComplete || !cvcComplete || isProcessing}
                        style={{
                            width: "100%",
                            height: "45px",
                            background: (!cardComplete || !expiryComplete || !cvcComplete || isProcessing)
                                ? "#cfd8dc"
                                : "linear-gradient(90deg, #0069d9, #0053ba)",
                            border: "none",
                            borderRadius: "8px",
                            color: "#fff",
                            fontWeight: "600",
                            fontSize: "16px",
                            cursor: (!cardComplete || !expiryComplete || !cvcComplete || isProcessing)
                                ? "not-allowed"
                                : "pointer",
                            transition: "all 0.3s ease",
                        }}
                    >
                        {isLoading ? <CircularProgress size={22} style={{ color: "#fff" }} /> : "Process Payment"}
                    </button>

                </Form>
            </Container>
        </div>
    );
}

export default function PaymentModal({
    paymentModal,
    setPaymentModal,
    amount
}) {
    return (
        <Modal
            show={paymentModal}
            className="checkin-dates-modal"
            onHide={() => setPaymentModal(false)}
            centered
        >
            <Modal.Header
                style={{ backgroundColor: "#0069d9", color: "white" }}
                closeButton
            >
                <Modal.Title>Payment</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Elements stripe={stripePromise}>
                    <StripeCheckoutForm amount={amount} />
                </Elements>
            </Modal.Body>
        </Modal>
    );
}